import React from "react";
import { ChevronLeft } from "lucide-react";
import { LOCATIONS } from "../config/locations";

const DEFAULT_EMAIL = "privacy@clinic-book.com";

// Resolve which business entity these legal terms apply to (via ?slug=)
const resolveEntity = () => {
  const slug = new URLSearchParams(window.location.search).get("slug") || "";
  const loc = LOCATIONS[slug];
  if (loc) {
    return {
      name: loc.legalName || loc.spaName,
      brand: loc.spaName,
      address: loc.address || "",
      phone: loc.phone || "",
      email: loc.privacyEmail || DEFAULT_EMAIL,
      backTo: `/${slug}`,
    };
  }
  // Nationwide / unknown → generic operator
  return { name: "Clinic Book", brand: "Clinic Book", address: "", phone: "", email: DEFAULT_EMAIL, backTo: "/" };
};

const Shell = ({ title, entity, children }) => (
  <div className="min-h-screen bg-brand-ivory">
    <div className="w-full bg-brand-sage py-2.5 text-center">
      <p className="font-sans text-[13px] font-medium tracking-wide text-brand-ivory">{entity.brand}</p>
    </div>
    <div className="mx-auto max-w-3xl px-5 py-12 md:px-8">
      <a href={entity.backTo} data-testid="legal-back-link" className="mb-6 inline-flex items-center gap-1 font-sans text-sm text-brand-muted transition hover:text-brand-ink">
        <ChevronLeft size={16} /> Back
      </a>
      <h1 className="font-serif text-3xl text-brand-ink md:text-4xl">{title}</h1>
      <p className="mt-2 font-sans text-sm text-brand-muted">{entity.name} · Last updated {new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}</p>
      <div className="mt-8 space-y-6">{children}</div>
    </div>
  </div>
);

const H = ({ children }) => <h2 className="font-serif text-xl text-brand-ink">{children}</h2>;
const P = ({ children }) => <p className="font-sans text-sm leading-relaxed text-brand-ink/80">{children}</p>;

export const PrivacyPolicy = () => {
  const e = resolveEntity();
  return (
    <Shell title="Privacy Policy" entity={e}>
      <div className="space-y-2">
        <P>
          {e.name} ("we", "us", "our") respects your privacy. This Privacy Policy explains how we collect,
          use, and protect the information you provide when you book an appointment or contact us through our website.
        </P>
      </div>

      <div className="space-y-2">
        <H>Information We Collect</H>
        <P>
          When you submit a booking or contact request, we collect the information you provide, which may include your
          name, phone number, email address, and appointment preferences.
        </P>
      </div>

      <div className="space-y-2">
        <H>How We Use Your Information</H>
        <P>
          We use your information to schedule and confirm your appointment, send you appointment reminders and
          confirmations, respond to your inquiries, and provide customer service related to your visit.
        </P>
      </div>

      <div className="space-y-2">
        <H>SMS / Text Messaging</H>
        <P>
          By providing your mobile phone number and opting in, you consent to receive appointment reminder and
          confirmation text messages from {e.name}. Message frequency may vary. Message and data rates may apply.
          You can opt out at any time by replying STOP, or reply HELP for assistance.
        </P>
        <P>
          <strong>
            No mobile information will be shared with third parties or affiliates for marketing or promotional purposes.
            Text messaging originator opt-in data and consent will not be shared with any third parties.
          </strong>
        </P>
      </div>

      <div className="space-y-2">
        <H>How We Share Information</H>
        <P>
          We do not sell your personal information. We may share information with trusted service providers who help us
          operate our scheduling and communications (for example, our booking and messaging platforms) solely to provide
          these services to you, and only as permitted by law. As stated above, SMS opt-in consent and phone numbers
          collected for SMS are never shared with third parties or affiliates for marketing purposes.
        </P>
      </div>

      <div className="space-y-2">
        <H>Data Security & Retention</H>
        <P>
          We use reasonable administrative and technical safeguards to protect your information and retain it only as long
          as needed to provide our services and comply with legal obligations.
        </P>
      </div>

      <div className="space-y-2">
        <H>Your Choices</H>
        <P>
          You may opt out of text messages at any time by replying STOP. You may request that we update or delete your
          information by contacting us using the details below.
        </P>
      </div>

      <div className="space-y-2">
        <H>Contact Us</H>
        <P>
          Questions about this Privacy Policy? Email us at{" "}
          <a href={`mailto:${e.email}`} className="text-brand-sage underline">{e.email}</a>
          {e.phone ? <> or call {e.phone}</> : null}
          {e.address ? <>. {e.address}.</> : "."}
        </P>
      </div>
    </Shell>
  );
};

export const TermsOfService = () => {
  const e = resolveEntity();
  return (
    <Shell title="Terms & SMS Messaging Terms" entity={e}>
      <div className="space-y-2">
        <H>SMS Program Description</H>
        <P>
          {e.name} sends appointment reminder and confirmation text messages to clients who opt in by submitting their
          mobile number through our website. This is a transactional messaging program related to your appointment.
        </P>
      </div>

      <div className="space-y-2">
        <H>Consent</H>
        <P>
          By checking the consent box and submitting your number, you agree to receive appointment reminder and
          confirmation SMS messages from {e.name} at the mobile number provided.
        </P>
      </div>

      <div className="space-y-2">
        <H>Message Frequency</H>
        <P>Message frequency varies based on your appointment activity.</P>
      </div>

      <div className="space-y-2">
        <H>Cost</H>
        <P>Message and data rates may apply, depending on your mobile carrier plan.</P>
      </div>

      <div className="space-y-2">
        <H>Opt-Out & Help</H>
        <P>
          To stop receiving messages, reply <strong>STOP</strong> at any time. For assistance, reply <strong>HELP</strong>{" "}
          or email <a href={`mailto:${e.email}`} className="text-brand-sage underline">{e.email}</a>. After you send STOP,
          we will send one confirmation message and then stop sending SMS messages.
        </P>
      </div>

      <div className="space-y-2">
        <H>Carriers</H>
        <P>
          Carriers are not liable for delayed or undelivered messages. Supported carriers may change without notice.
        </P>
      </div>

      <div className="space-y-2">
        <H>Privacy</H>
        <P>
          Your information is handled in accordance with our{" "}
          <a href={`/privacy${window.location.search}`} className="text-brand-sage underline">Privacy Policy</a>.
        </P>
      </div>

      <div className="space-y-2">
        <H>Contact</H>
        <P>
          Questions? Email <a href={`mailto:${e.email}`} className="text-brand-sage underline">{e.email}</a>
          {e.phone ? <> or call {e.phone}</> : null}.
        </P>
      </div>
    </Shell>
  );
};
